import {
  MultipleChoice,
  MultipleChoiceGroup,
} from '@ulysses-inc/harami_api_client'
import { Alert, Drawer } from 'antd'
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { multipleChoiceColors } from 'src/features/theme/MultipleChoiceColor'
import AddChoiceRow from './components/AddChoiceRow/AddChoiceRow'
import CommonMultipleChoiceButtonsRow from './components/ButtonsRow/CommonMultipleChoiceButtonsRow'
import TemplateSpecificMultipleChoiceButtonsRow from './components/ButtonsRow/TemplateSpecificMultipleChoiceButtonsRow'
import TableRow from './components/TableRow/TableRow'
import { useMultipleChoiceGroupRelatedTemplates } from './hooks/useMultipleChoiceGroupRelatedTemplates'
import {
  DrawerBodyStyle,
  DrawerHeaderStyle,
} from './style/MultipleChoiceDrawer.styled'

interface IMultipleChoices {
  [key: number]: MultipleChoice
}

type TMultipleChoiceStateSetter = Dispatch<SetStateAction<IMultipleChoices>>
export type TMultipleChoicesState = [
  IMultipleChoices,
  TMultipleChoiceStateSetter,
]

interface OwnProps {
  isVisible: boolean
  updateMultipleChoiceGroup?: MultipleChoiceGroup
  isTemplateSpecific: boolean
  onClose: () => void
  /**
   * ドロワーの z-index。既定は通常の質問編集 (ResponseDropDown: 1050) より前面に出す 1100。
   * 表編集のようにフルスクリーン Dialog (1300) の上で開く場合は、より大きい値を渡す。
   */
  zIndex?: number
}

const initialMultipleChoice = (id: number): MultipleChoice => ({
  id,
  response: '',
  color: multipleChoiceColors.default,
  isInvalid: 0,
  isExcludedFromScoring: false,
  score: 0,
  isDefault: 0,
})

// 回答の選択肢を登録するために右から出てくるドロワー
const MultipleChoiceDrawer: FC<OwnProps> = ({
  isVisible,
  updateMultipleChoiceGroup: multipleChoiceGroupForUpdate,
  isTemplateSpecific,
  onClose,
  zIndex = 1100,
}) => {
  const multipleChoicesState = useState<{
    [key: number]: MultipleChoice
  }>({})
  const [multipleChoices, setMultipleChoices] = multipleChoicesState
  const [lastKey, setLastKey] = useState(0)

  useEffect(() => {
    let tempChoices: { [key: number]: MultipleChoice } = {}
    let maxId = 0
    if (multipleChoiceGroupForUpdate) {
      multipleChoiceGroupForUpdate.responses.forEach(res => {
        const resId = res.id ?? 0
        tempChoices = { ...tempChoices, [resId]: res }
        maxId = maxId < resId ? resId : maxId
      })
      // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
      setLastKey(maxId)
    } else {
      const firstKey = 1
      tempChoices = { [firstKey]: initialMultipleChoice(firstKey) }
      setLastKey(firstKey)
    }
    setMultipleChoices(tempChoices)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multipleChoiceGroupForUpdate])

  const addNewMultipleChoice = () => {
    const newKey = lastKey + 1
    setMultipleChoices({
      ...multipleChoices,
      [newKey]: initialMultipleChoice(newKey),
    })
    setLastKey(newKey)
  }

  const {
    relatedTemplates,
    isFetchingRelatedTemplates,
    isErrorRelatedTemplates,
  } = useMultipleChoiceGroupRelatedTemplates(multipleChoiceGroupForUpdate)

  return (
    <Drawer
      title="選択肢"
      maskClosable={false}
      placement="right"
      open={isVisible}
      width="650"
      headerStyle={DrawerHeaderStyle}
      bodyStyle={DrawerBodyStyle}
      closable={false}
      zIndex={zIndex}
    >
      <TableRow {...{ multipleChoicesState, lastKey, addNewMultipleChoice }} />
      <AddChoiceRow {...{ addNewMultipleChoice }} />
      {isTemplateSpecific ? (
        <TemplateSpecificMultipleChoiceButtonsRow
          {...{
            multipleChoicesState,
            multipleChoiceGroupForUpdate,
            onClose,
            relatedTemplates,
            isFetchingRelatedTemplates,
            isErrorRelatedTemplates,
          }}
        />
      ) : (
        <CommonMultipleChoiceButtonsRow
          {...{
            multipleChoicesState,
            multipleChoiceGroupForUpdate,
            onClose,
            relatedTemplates,
            isFetchingRelatedTemplates,
            isErrorRelatedTemplates,
          }}
        />
      )}
      {isErrorRelatedTemplates && (
        <Alert
          type="error"
          message="関連ひな形の取得に失敗しました"
          description="ページを再読み込みして再度お試しください。"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
    </Drawer>
  )
}

export default MultipleChoiceDrawer
